import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Package, Truck, Upload, X } from "lucide-react";
import { useAuth, homeFor } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import AuthLayout from "@/components/AuthLayout";
import { CountryCurrency, GitSection, TermsCheckbox } from "@/components/RegisterSections";
import { t } from "@/lib/i18n";

const GIT_DOC = "Goods in Transit (GIT) Insurance";

const DOCS = {
  shipper: ["Company Registration Certificate", "Tax / VAT Certificate", "Proof of Business Address", "Director / Officer ID"],
  transporter: ["Company Registration Certificate", "Vehicle Registration / Roadworthy", "Operator Licence / Cross-Border Permits"],
};

const FIELDS = [
  ["name", "Contact person", "text"], ["company_name", "Company name", "text"], ["email", "Email", "email"],
  ["password", "Password", "password"], ["phone", "Phone / WhatsApp", "tel"], ["registration_number", "Company reg. number", "text"],
  ["vat_number", "VAT number (optional)", "text"],
];

function DocInput({ label, file, onChange, idx }) {
  return (
    <label className={`flex items-center gap-3 p-3 rounded-md border border-dashed cursor-pointer transition-colors ${file ? "border-emerald-400 bg-emerald-50/50" : "border-slate-300 hover:border-amber-500"}`}>
      <Upload className="w-4 h-4 text-slate-500 shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">{label}</span>
        <span className="block text-xs text-slate-500 truncate">{file ? file.name : t("PDF, JPG or PNG · max 10MB")}</span>
      </span>
      {file && <X className="w-4 h-4 text-slate-400" onClick={(e) => { e.preventDefault(); onChange(null); }} />}
      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid={`register-doc-input-${idx}`} onChange={(e) => onChange(e.target.files[0] || null)} />
    </label>
  );
}

export default function Register() {
  const [params] = useSearchParams();
  const { register, meta } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(params.get("role") === "transporter" ? "transporter" : "shipper");
  const [form, setForm] = useState({ address: "" });
  const [files, setFiles] = useState({});
  const [gitFile, setGitFile] = useState(null);
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agree) return setError(t("You must accept the Terms & Conditions to register."));
    const docs = Object.entries(files).filter(([, f]) => f);
    if (role === "transporter") {
      if (!gitFile) return setError(t("Upload your Goods in Transit (GIT) insurance certificate."));
      docs.unshift([GIT_DOC, gitFile]);
    }
    if (!docs.length) return setError(t("Upload at least one registration document."));
    const fd = new FormData();
    fd.append("role", role);
    fd.append("accept_terms", "true");
    Object.entries(form).filter(([k]) => role === "transporter" || !k.startsWith("git_")).forEach(([k, v]) => fd.append(k, v || ""));
    docs.forEach(([dt, f]) => { fd.append("doc_types", dt); fd.append("files", f); });
    setBusy(true);
    try {
      const u = await register(fd);
      navigate(homeFor(u));
    } catch (err) { setError(errMsg(err)); }
    setBusy(false);
  };

  const RoleBtn = ({ value, icon: Icon, title, desc }) => (
    <button type="button" onClick={() => { setRole(value); setFiles({}); }} data-testid={`register-role-${value}`}
      className={`text-left p-4 rounded-lg border-2 transition-colors ${role === value ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}>
      <Icon className="w-5 h-5 text-amber-600" />
      <p className="font-display font-bold mt-2">{title}</p>
      <p className="text-xs text-slate-600">{desc}</p>
    </button>
  );

  return (
    <AuthLayout wide title={t("Register your company")} subtitle={t("Every account is verified by an admin before it can post or bid.")}>
      <form onSubmit={submit} className="space-y-6" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <RoleBtn value="shipper" icon={Package} title={t("Shipper")} desc={t("I have commodities to move")} />
          <RoleBtn value="transporter" icon={Truck} title={t("Transporter")} desc={t("I have trucks to haul")} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, l, typ]) => (
            <div key={k}>
              <label className="hf-label">{t(l)}</label>
              <input type={typ} required={!k.includes("vat")} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="hf-input" data-testid={`register-${k}-input`} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="hf-label">{t("Business address")}</label>
            <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="hf-input" data-testid="register-address-input" />
          </div>
        </div>
        <CountryCurrency meta={meta} form={form} setForm={setForm} />
        {role === "transporter" && <GitSection form={form} setForm={setForm} file={gitFile} setFile={setGitFile} />}
        <div>
          <p className="hf-label">{t("Registration papers")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {DOCS[role].map((d, i) => <DocInput key={d} idx={i} label={t(d)} file={files[d]} onChange={(f) => setFiles({ ...files, [d]: f })} />)}
          </div>
        </div>
        <TermsCheckbox checked={agree} onChange={setAgree} />
        {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
        <button disabled={busy || !agree} className="hf-btn-primary w-full h-12" data-testid="register-submit-btn">{busy ? t("Uploading papers…") : t("Submit for verification")}</button>
        <p className="text-sm text-slate-600">{t("Already registered?")} <Link to="/login" className="text-amber-700 font-semibold">{t("Sign in")}</Link></p>
      </form>
    </AuthLayout>
  );
}
